import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

/** Legacy default: child if age <= 17 at arrival (adult from 18). */
export const DEFAULT_CHILD_MAX_AGE_YEARS = 17;

/** Backend {@link com.mikos.onlinecheckin.ows.reservation.mapper.ReservationMapper} uses {@code dd/MM/yyyy}. */
export function parseReservationArrivalDate(arrivalDateStr: string | undefined | null): Dayjs | null {
    if (arrivalDateStr == null || String(arrivalDateStr).trim() === "") return null;
    const s = String(arrivalDateStr).trim();
    const dmy = dayjs(s, "DD/MM/YYYY", true);
    if (dmy.isValid()) return dmy.startOf("day");
    const fallback = dayjs(s);
    return fallback.isValid() ? fallback.startOf("day") : null;
}

export function ageFullYearsOnReference(
    dob: { day?: number | null; month?: number | null; year?: number | null },
    ref: Dayjs
): number | null {
    const { day, month, year } = dob;
    if (day == null || month == null || year == null) return null;
    const birth = dayjs().year(year).month(month - 1).date(day).startOf("day");
    if (!birth.isValid()) return null;

    let age = ref.year() - birth.year();
    const birthdayAnniversary = birth.year(ref.year());
    if (ref.isBefore(birthdayAnniversary, "day")) age -= 1;
    return age;
}

export function isGuestSlotChild(adultCount: number | undefined, guestIndexZeroBased: number): boolean {
    const adults = Math.max(0, adultCount ?? 0);
    return guestIndexZeroBased >= adults;
}

export function adultMinAgeYears(childMaxAgeYears: number): number {
    return childMaxAgeYears + 1;
}

/**
 * DOB-only adult/child derivation used by {@link mapGuestsFromReservation} when a guest's kind
 * is not persisted on the backend. Returns null when disabled, DOB is missing, or age is unknown.
 */
export function deriveKindFromDob(
    dob: { day?: number | null; month?: number | null; year?: number | null } | null | undefined,
    arrival: Dayjs | null,
    childMaxAgeYears: number | null = DEFAULT_CHILD_MAX_AGE_YEARS
): "ADULT" | "CHILD" | null {
    if (childMaxAgeYears == null || !arrival || !dob) return null;
    const age = ageFullYearsOnReference(dob, arrival);
    if (age == null) return null;
    return age <= childMaxAgeYears ? "CHILD" : "ADULT";
}

export type DobSlotAgeIssue =
    | { ok: true }
    | { ok: false; kind: "adult_under_min_age" | "child_over_max_age" };

export function validateDobForAdultChildSlot(
    isChildSlot: boolean,
    dob: { day?: number | null; month?: number | null; year?: number | null } | null | undefined,
    arrival: Dayjs | null,
    childMaxAgeYears: number | null = DEFAULT_CHILD_MAX_AGE_YEARS
): DobSlotAgeIssue {
    if (childMaxAgeYears == null) return { ok: true };
    const d = dob ?? {};
    if (!arrival) return { ok: true };
    const age = ageFullYearsOnReference(d, arrival);
    if (age == null) return { ok: true };

    if (isChildSlot) {
        if (age > childMaxAgeYears) return { ok: false, kind: "child_over_max_age" };
        return { ok: true };
    }
    if (age <= childMaxAgeYears) return { ok: false, kind: "adult_under_min_age" };
    return { ok: true };
}

export function dobPickerBounds(
    isChildSlot: boolean,
    referenceArrival: Dayjs | null,
    childMaxAgeYears: number | null = DEFAULT_CHILD_MAX_AGE_YEARS
): { minDate: Dayjs; maxDate: Dayjs } {
    const today = dayjs().startOf("day");
    const minDefault = today.subtract(120, "year");
    if (childMaxAgeYears == null || !referenceArrival) {
        return { minDate: minDefault, maxDate: today };
    }
    const adultLatestBirth = referenceArrival
        .subtract(adultMinAgeYears(childMaxAgeYears), "year")
        .startOf("day");
    const childEarliestBirth = adultLatestBirth.add(1, "day");
    const pickEarlier = (a: Dayjs, b: Dayjs) => (a.isBefore(b) ? a : b);
    const pickLater = (a: Dayjs, b: Dayjs) => (a.isAfter(b) ? a : b);
    if (isChildSlot) {
        return { minDate: pickLater(minDefault, childEarliestBirth), maxDate: today };
    }
    return { minDate: minDefault, maxDate: pickEarlier(today, adultLatestBirth) };
}
